import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Card, CardContent, Alert, CircularProgress, Grid, IconButton } from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ZXingBarcodeScanner from '../components/ZXingBarcodeScanner';
import { productService } from '../services/productService';
import { stockEntryService } from '../services/stockEntryService';
import { stockExitService } from '../services/stockExitService';
import { dashboardService } from '../services/dashboardService';
import { formatCurrency } from '../utils/currency';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useStock } from '../contexts/StockContext';

const ScanProduct = ({ refreshUnreadNotifications }) => {
  const { t } = useLanguage();
  const { triggerRefresh } = useStock();
  const [scannerOpen, setScannerOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [product, setProduct] = useState(null);
  const [lastBarcode, setLastBarcode] = useState(null);
  const [movements, setMovements] = useState([]);
  const [loadingMovement, setLoadingMovement] = useState(false);
  const [adjustment, setAdjustment] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    loadMovements();
  }, []);

  const handleScan = async (barcode) => {
    setLoading(true);
    setError(null);
    setProduct(null);
    setLastBarcode(barcode);
    try {
      const res = await productService.getProducts({ barcode });
      if (res.products && res.products.length > 0) {
        setProduct(res.products[0]);
        setAdjustment(0);
        loadMovements();
      } else {
        setError('Aucun produit trouvé pour ce code-barres.');
      }
    } catch (e) {
      setError("Erreur lors de la recherche du produit.");
    } finally {
      setLoading(false);
    }
  };

  const reloadProduct = async () => {
    if (!product) return;
    try {
      const res = await productService.getProducts({ barcode: product.barcode });
      if (res.products && res.products.length > 0) {
        setProduct(res.products[0]);
      }
    } catch {}
  };

  const loadMovements = async () => {
    setLoadingMovement(true);
    try {
      const dashboard = await dashboardService.getDashboard();
      // On fusionne les entrées et sorties, puis on trie par date décroissante
      const allMovements = [
        ...(dashboard.recent_entries || []).map(m => ({ ...m, type: 'entry' })),
        ...(dashboard.recent_exits || []).map(m => ({ ...m, type: 'exit' })),
      ];
      allMovements.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setMovements(allMovements.slice(0, 7));
    } catch (e) {
      setMovements([]);
    } finally {
      setLoadingMovement(false);
    }
  };

  const handleIncrement = () => setAdjustment(a => a + 1);
  const handleDecrement = () => {
    if (product && adjustment > -product.current_stock) {
      setAdjustment(a => a - 1);
    }
  };

  const canValidate = () => {
    if (!product) return false;
    if (adjustment === 0) return false;
    if (adjustment < 0 && Math.abs(adjustment) > product.current_stock) return false;
    return true;
  };

  const handleValidate = async () => {
    if (!product || adjustment === 0) return;
    setLoadingMovement(true);
    try {
      if (adjustment > 0) {
        await stockEntryService.createStockEntry({
          product_id: product.id,
          quantity: adjustment,
          unit_price: product.price,
          reference: 'Ajustement via scan',
          comment: 'Ajout via scan',
        });
      } else if (adjustment < 0) {
        await stockExitService.createStockExit({
          product_id: product.id,
          quantity: Math.abs(adjustment),
          unit_price: product.price,
          reason: 'Ajustement via scan',
          comment: 'Sortie via scan',
        });
      }
      setAdjustment(0);
      await reloadProduct();
      loadMovements();
      if (typeof refreshUnreadNotifications === 'function') {
        refreshUnreadNotifications();
      }
      if (refreshUnreadNotifications) {
        refreshUnreadNotifications();
      }
      triggerRefresh(); // Rafraîchir le dashboard
    } catch (e) {
      setError("Erreur lors de l'ajustement du stock.");
    } finally {
      setLoadingMovement(false);
    }
  };

  const handleAddProduct = () => {
    navigate('/products', { state: { prefillBarcode: lastBarcode } });
    // Réinitialiser tous les états liés au scan pour éviter la boucle
    setLastBarcode(null);
    setProduct(null);
    setAdjustment(0);
    setError(null);
  };

  return (
    <Box>
      <Typography variant="h4" mb={3}>
        {t('scan.title')}
      </Typography>
      <Button
        variant="contained"
        startIcon={<QrCodeScannerIcon />}
        onClick={() => setScannerOpen(true)}
        sx={{ mb: 3 }}
      >
        {t('scan.startScan')}
      </Button>
      <ZXingBarcodeScanner
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onScan={handleScan}
      />
      {loading && <CircularProgress sx={{ mt: 2 }} />}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
          {lastBarcode && (
            <Box mt={2}>
              <Button variant="outlined" color="primary" onClick={handleAddProduct}>
                {t('scan.addThisProduct')} ({lastBarcode})
              </Button>
            </Box>
          )}
        </Alert>
      )}
      {product && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6">{product.name}</Typography>
            <Grid container spacing={2} mt={1} alignItems="center">
              <Grid item xs={12} sm={6}>
                <Typography>{t('products.price')} : <b>{formatCurrency(product.price, product.currency || 'EUR')}</b></Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography>{t('products.supplier')} : <b>{product.supplier_name || 'N/A'}</b></Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography>{t('products.barcode')} : <b>{product.barcode || 'N/A'}</b></Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2" sx={{ minWidth: 80 }}>{t('products.currentStock')} : <b>{product.current_stock}</b></Typography>
                  <IconButton color="error" onClick={handleDecrement} disabled={loadingMovement || adjustment <= -product.current_stock}>
                    <RemoveIcon />
                  </IconButton>
                  <Typography variant="h6" sx={{ minWidth: 40, textAlign: 'center' }}>{adjustment}</Typography>
                  <IconButton color="success" onClick={handleIncrement} disabled={loadingMovement}>
                    <AddIcon />
                  </IconButton>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    sx={{ ml: 2 }}
                    disabled={loadingMovement || !canValidate()}
                    onClick={handleValidate}
                  >
                    {t('common.validate')}
                  </Button>
                </Box>
                <Typography variant="caption" color="textSecondary">{t('scan.adjustmentToApply')}</Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Derniers mouvements de stock */}
      <Box mt={4}>
        <Typography variant="subtitle1" align="center" fontWeight="bold" gutterBottom>
          {t('scan.recentMovements')}
        </Typography>
        {loadingMovement ? (
          <Box display="flex" justifyContent="center" mt={2}><CircularProgress size={24} /></Box>
        ) : (
          <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
            {movements.length === 0 && <Typography color="textSecondary">{t('products.noRecentMovements')}</Typography>}
            {movements.map((m, idx) => (
              <Box key={idx} display="flex" alignItems="center" gap={1}>
                {/* ⚠️ `'red'` / `'green'` — des couleurs CSS NOMMÉES, donc fixes
                    dans les deux modes. En mode sombre, le rouge pur (#ff0000)
                    sur le fond sombre est criard et le vert pur peu lisible ;
                    la palette, elle, décline ses teintes par mode.
                    ⚠️ ET LE SENS RESTE CELUI DE LA DIRECTION, pas d'un état :
                    une sortie n'est pas une erreur. `error`/`success` sont les
                    deux seules entrées de la palette portant ces teintes — on
                    emprunte leur COULEUR, pas leur sémantique. */}
                {m.type === 'exit' ? (
                  <ArrowDownwardIcon sx={{ color: 'error.main' }} fontSize="small" />
                ) : (
                  <ArrowUpwardIcon sx={{ color: 'success.main' }} fontSize="small" />
                )}
                <Typography fontWeight={m.type === 'exit' ? 'bold' : 'normal'}>
                  {m.product_name || m.product?.name} <b>({m.quantity})</b>
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ScanProduct; 